export type EditorCaretPosition = {
  x: number;
  y: number;
  caretTop: number;
};

type GetEditorCaretPositionArgs = {
  editorRoot: HTMLElement | null;
  fallbackAnchorElement?: HTMLElement | null;
  fallbackOffset?: number;
};

function hasCaretCoordinates(rect: DOMRect): boolean {
  return (
    Number.isFinite(rect.left) &&
    Number.isFinite(rect.bottom) &&
    (rect.width > 0 ||
      rect.height > 0 ||
      rect.left > 0 ||
      rect.top > 0 ||
      rect.bottom > 0)
  );
}

function resolveEditorContainer(editorRoot: HTMLElement): HTMLElement {
  if (editorRoot.matches('[contenteditable="true"]')) {
    return editorRoot;
  }

  const contentEditable = editorRoot.querySelector('[contenteditable="true"]');
  return contentEditable instanceof HTMLElement ? contentEditable : editorRoot;
}

function isSelectionInsideContainer({
  selection,
  container,
}: {
  selection: Selection;
  container: HTMLElement;
}): boolean {
  const anchorNode = selection.anchorNode;
  const focusNode = selection.focusNode;
  if (!anchorNode || !focusNode) {
    return false;
  }

  if (!container.contains(anchorNode) || !container.contains(focusNode)) {
    return false;
  }

  if (selection.rangeCount === 0) {
    return false;
  }

  try {
    return selection.getRangeAt(0).intersectsNode(container);
  } catch {
    return true;
  }
}

function getCollapsedRangeRect(range: Range): DOMRect | null {
  const collapsedRects = Array.from(range.getClientRects()).filter((rect) =>
    hasCaretCoordinates(rect),
  );
  if (collapsedRects.length > 0) {
    return collapsedRects[collapsedRects.length - 1];
  }

  const rect = range.getBoundingClientRect();
  return hasCaretCoordinates(rect) ? rect : null;
}

function getCaretRectFromAnchorElement({
  anchorElement,
  textOffset,
}: {
  anchorElement: HTMLElement;
  textOffset: number;
}): DOMRect | null {
  const range = document.createRange();
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(
    anchorElement,
    NodeFilter.SHOW_TEXT,
  );

  let node = walker.nextNode();
  while (node) {
    if (node instanceof Text) {
      textNodes.push(node);
    }
    node = walker.nextNode();
  }

  if (textNodes.length === 0) {
    try {
      range.selectNodeContents(anchorElement);
      range.collapse(true);
      return getCollapsedRangeRect(range);
    } catch {
      return null;
    }
  }

  let remaining = textOffset;
  for (const textNode of textNodes) {
    const length = textNode.data.length;
    if (remaining <= length) {
      try {
        range.setStart(textNode, remaining);
        range.collapse(true);
        return getCollapsedRangeRect(range);
      } catch {
        return null;
      }
    }
    remaining -= length;
  }

  const lastNode = textNodes[textNodes.length - 1];
  try {
    range.setStart(lastNode, lastNode.data.length);
    range.collapse(true);
    return getCollapsedRangeRect(range);
  } catch {
    return null;
  }
}

function getCaretRectInContainer({
  container,
}: {
  container: HTMLElement;
}): DOMRect | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  if (!isSelectionInsideContainer({ selection, container })) {
    return null;
  }

  const focusNode = selection.focusNode;
  if (!focusNode) {
    return null;
  }

  const focusRange = document.createRange();
  try {
    focusRange.setStart(focusNode, selection.focusOffset);
    focusRange.collapse(true);
    const focusRect = getCollapsedRangeRect(focusRange);
    if (focusRect) {
      return focusRect;
    }
  } catch {
    return null;
  }

  const range = selection.getRangeAt(0);
  const rangeRects = Array.from(range.getClientRects()).filter((rect) =>
    hasCaretCoordinates(rect),
  );

  if (rangeRects.length > 0) {
    return rangeRects[rangeRects.length - 1];
  }

  const rangeRect = range.getBoundingClientRect();
  return hasCaretCoordinates(rangeRect) ? rangeRect : null;
}

function getFallbackAnchorPosition({
  fallbackAnchorElement,
  fallbackOffset,
}: {
  fallbackAnchorElement: HTMLElement;
  fallbackOffset: number;
}): EditorCaretPosition {
  const caretRect = getCaretRectFromAnchorElement({
    anchorElement: fallbackAnchorElement,
    textOffset: fallbackOffset,
  });
  if (caretRect) {
    return {
      x: caretRect.left,
      y: caretRect.bottom,
      caretTop: caretRect.top,
    };
  }

  const rect = fallbackAnchorElement.getBoundingClientRect();
  return {
    x: rect.left + fallbackOffset * 8,
    y: rect.bottom,
    caretTop: rect.top,
  };
}

export function getEditorCaretPosition({
  editorRoot,
  fallbackAnchorElement,
  fallbackOffset = 0,
}: GetEditorCaretPositionArgs): EditorCaretPosition | undefined {
  if (!editorRoot) {
    return undefined;
  }

  const editorContainer = resolveEditorContainer(editorRoot);

  if (
    fallbackAnchorElement &&
    editorContainer.contains(fallbackAnchorElement)
  ) {
    const lexicalCaret = getCaretRectFromAnchorElement({
      anchorElement: fallbackAnchorElement,
      textOffset: fallbackOffset,
    });
    if (lexicalCaret) {
      return {
        x: lexicalCaret.left,
        y: lexicalCaret.bottom,
        caretTop: lexicalCaret.top,
      };
    }

    return getFallbackAnchorPosition({
      fallbackAnchorElement,
      fallbackOffset,
    });
  }

  const caretRect = getCaretRectInContainer({ container: editorContainer });
  if (caretRect) {
    return {
      x: caretRect.left,
      y: caretRect.bottom,
      caretTop: caretRect.top,
    };
  }

  const rootRect = editorRoot.getBoundingClientRect();
  return {
    x: rootRect.left,
    y: rootRect.bottom,
    caretTop: rootRect.top,
  };
}
